import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

export interface NotificationStatusDto {
  smsConfigured: boolean;
  whatsAppConfigured: boolean;
}

export interface SendNotificationRequest {
  channel: number; // 1 = SMS, 2 = WhatsApp
  phone: string;
  message: string;
}

export interface SendNotificationResult {
  success: boolean;
  channel: number;
  toPhone: string;
  providerResponse: string | null;
}

export interface NotificationLogDto {
  notificationLogId: string;
  channel: number;
  toPhone: string;
  body: string;
  status: number; // 0 = Pending/queued, 1 = Sent, 2 = Failed
  attempts: number;
  providerResponse: string | null;
  createdAt: string;
}

export interface NotificationSettingsDto {
  orderPaidEnabled: boolean;
  orderPaidChannel: number;
  orderPaidTemplate: string | null;
  loyaltyEarnEnabled: boolean;
  loyaltyEarnChannel: number;
  loyaltyEarnTemplate: string | null;
  lowStockEnabled: boolean;
  lowStockChannel: number;
  lowStockPhone: string | null;
  lowStockTemplate: string | null;
  smsSenderId: string | null;
}

export function useNotificationSettings() {
  return useQuery({
    queryKey: ['notification-settings'],
    queryFn: async () => (await apiClient.get<NotificationSettingsDto>('/api/notifications/settings')).data,
  });
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: NotificationSettingsDto) =>
      (await apiClient.put<NotificationSettingsDto>('/api/notifications/settings', req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-settings'] }),
  });
}

export function useNotificationStatus() {
  return useQuery({
    queryKey: ['notification-status'],
    queryFn: async () => (await apiClient.get<NotificationStatusDto>('/api/notifications/status')).data,
  });
}

export function useNotificationLog(take = 100) {
  return useQuery({
    queryKey: ['notification-log', take],
    queryFn: async () => (await apiClient.get<NotificationLogDto[]>('/api/notifications/log', { params: { take } })).data,
  });
}

export function useSendNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: SendNotificationRequest) =>
      (await apiClient.post<SendNotificationResult>('/api/notifications/send', req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-log'] }),
  });
}
